"use client";

import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/icons";
import { haptics } from "@/lib/haptics";
import {
  BAR_WEIGHTS,
  PLATE_SETS,
  computeTotal,
  decomposeTargetClosest,
  defaultBarWeight,
} from "@/lib/plate-math";
import type { WeightUnit } from "@/lib/stats";

/**
 * NumericKeypadPlateCalc — plate calculator panel (v9.218).
 *
 * Sits to the left of the keypad in the three-panel carousel:
 *   plateCalc ← keypad → calculator
 *
 * ## v9.218 changes (post-second-review)
 *
 *   - BAR cell moved INTO the grid as the 8th slot. Sub-header
 *     no longer carries the bar chip — it's just the helper hint
 *     + diff readout now. Simplifies the layout: 7 plates + 1
 *     bar cell fills the 8-slot grid exactly.
 *   - Inactive plate cells muted further (bg-surface-2/40 +
 *     text-fg-muted/60) so the brand-fill active cells dominate
 *     the visual hierarchy.
 *   - Long-press on a plate cell resets that plate to 0
 *     (~500 ms hold, light haptic confirmation). Combines with
 *     the existing tap-to-increment and corner-minus-to-decrement
 *     into a complete +1 / −1 / reset gesture vocabulary per cell.
 *   - TARGET in the header is now a button that returns to the
 *     keypad — the canonical "edit target" path. The header's
 *     `<` back arrow does the same; making TARGET tappable too
 *     means lifters who think "the target is wrong, fix it"
 *     can act on the affordance they're looking at.
 *
 * ## Interactions
 *
 *   Plate cell tap         -> +1 (light haptic)
 *   Plate cell corner `-`  -> -1 (light haptic; only when count > 0)
 *   Adjust plates          -> the plate grid
 *   Adjust bar / Percent   -> the row of doors becomes that picker
 *   Reset (plate grid)     -> clear every plate, the bar stays
 *   Bottom-left button     -> up one level; at the top, the numpad
 *   `Apply N KG`           -> write TOTAL back to keypad
 */

export type NumericKeypadPlateCalcProps = {
  initialValue: string;
  unit: WeightUnit;
  onApply: (total: number) => void;
  /** Back to the numpad panel.
   *
   *  KEYPAD_MODERN — replaces the v9.236 pair of cycle callbacks
   *  (`onCyclePrev` / `onCycleNext` plus their panel-name labels). That
   *  machinery was built for a three-panel carousel; v9.265 retired the
   *  calculator panel and left a two-panel set, at which point "previous"
   *  and "next" were the same place and the header carried two arrows that
   *  did the same thing. One named button beats two mystery chevrons. If a
   *  third panel ever arrives, the loop can come back with it. */
  onBackToKeypad: () => void;
};

// v9.246 — Variant A (visual barbell + colour-coded plate cells)
// promoted to default after admin-preview comparison. Variants B
// (math expression) and C (corner colour dots) retired. Per-variant
// branching removed; what shipped reflects the PlatesA layout the
// user picked, plus all the iterative polish:
//   - Barbell SVG above the plate grid, with stepped widths and
//     real powerlifting plate colours
//   - HTML labels above the SVG (per-side / bar / per-side) at
//     constant pixel size regardless of SVG zoom
//   - Active plate cells fill with the plate's real colour
//   - BAR cell fills with the SVG bar's grey when bar > 0
//   - X reset restores the target's auto-decomposition (was: blank)
//   - Width-based plate cap (220 SVG units) prevents extreme
//     loads from squashing the visual

// v9.245 — Standard powerlifting plate colours, shared between
// Variant C (cell-dot mnemonic) and Variant A (SVG plate fill).
// Module-scope so both consumers reference the same map; no per-
// render allocation.
//
// KG — IPF / IWF calibrated plate colours:
//   25  = red       (the "red family" — large calibrated plate)
//   20  = blue
//   15  = yellow
//   10  = green
//   5   = white
//   2.5 = red       (small change plate, matches 25 kg colour
//                    family; SIZE differentiates them visually)
//   1.25 = dark grey (NO official standard — not an IPF/IWF
//                    competition plate. Common in UK/Euro
//                    commercial gyms as the smallest fractional;
//                    typically chrome or black at the rack. Dark
//                    grey reads as "small generic plate" without
//                    claiming a colour standard that doesn't
//                    exist.)
//
// LB — USAPL / IWF bumper-plate colour convention (LB plates
// map to the nearest KG colour family):
//   45 = blue   (matches 20 kg)
//   35 = yellow (matches 15 kg)
//   25 = green  (matches 10 kg)
//   10 = white  (matches 5 kg)
//   5  = red    (matches 2.5 kg red change family)
//   2.5  = dark grey (no convention — generic small plate)
//   1.25 = dark grey (no convention — generic micro plate)
//
// v9.245.3 fixes: KG 2.5 was light grey (now red per IPF
// standard). LB was offset by one position in the colour cycle
// (45 was red, should be blue); rotated the whole LB ramp + added
// the 1.25 LB entry that was missing.
/** Exported so the feature tour can draw plate chips from the SAME source
 *  as the real calculator. It hand-rolled its own three-colour row before,
 *  which is exactly how a mock ends up showing a product that no longer
 *  exists. Denominations come from PLATE_SETS for the same reason. */
export const PLATE_COLOURS_KG: Record<number, string> = {
  25: "#dc2626",
  20: "#2563eb",
  15: "#eab308",
  10: "#16a34a",
  5: "#ffffff",
  2.5: "#dc2626",
  1.25: "#4b5563",
};
export const PLATE_COLOURS_LB: Record<number, string> = {
  45: "#2563eb",
  35: "#eab308",
  25: "#16a34a",
  10: "#ffffff",
  5: "#dc2626",
  2.5: "#4b5563",
  1.25: "#4b5563",
};

export function NumericKeypadPlateCalc({
  initialValue,
  unit,
  onApply,
  onBackToKeypad,
}: NumericKeypadPlateCalcProps) {
  const denoms = PLATE_SETS[unit];
  const initialTarget = useMemo(() => {
    const parsed = Number(initialValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [initialValue]);

  const [bar, setBar] = useState<number>(() => defaultBarWeight(unit));
  // THERE IS ONE UNIT IN THIS PANEL, and it is the workout's.
  // A display-unit toggle used to live here: plates in kg, readout in lb.
  // It was removed because it converted plate DENOMINATIONS, which
  // produced plates that do not exist (a 25 kg drawn as 55.12 lb). This
  // note is what was left of it - a docblock with no statement under it,
  // still telling the next reader that two units are in play. They are
  // not. `unit` is the workout unit and everything on screen is in it.

  // v9.261 — local-target override for the inline edit overlay
  // (TARGET tap / % button). When set, plate-calc renders this
  // value as the effective target instead of `initialTarget`
  // (which comes from the keypad's `value` prop). Lets the lifter
  // iterate on target inside plate-calc — typing a new value or
  // applying a percentage — without bouncing through the keypad
  // panel. The Apply button at the bottom writes the CURRENT
  // TOTAL back to the host on commit, so localTarget is purely a
  // staging concept inside this panel.
  //
  // Resets when initialTarget (the prop-derived target) changes —
  // i.e. the user committed Apply or re-opened the panel with a
  // fresh keypad value.
  const [localTarget, setLocalTarget] = useState<number | null>(null);
  const effectiveTarget = localTarget ?? initialTarget;

  /** Has the lifter set the plates themselves?
   *
   *  Until they have, this panel is SOLVING: it is trying to hit the target,
   *  so a change of bar should re-solve for the new one. The moment they add
   *  or remove a plate by hand it stops solving and starts REPORTING a load
   *  they are building, and re-solving would throw their work away.
   *
   *  Which is exactly what it did. Reported from a device: load the bar up
   *  on Adjust plates, change the bar weight, and every plate chosen is
   *  replaced by a fresh decomposition of the target. The bar effect had no
   *  way to tell the two situations apart, because nothing recorded which
   *  one it was in.
   */
  const [platesEdited, setPlatesEdited] = useState(false);

  /** A new number from the numpad is a new problem: drop any percentage and
   *  go back to solving it.
   *
   *  Adjusted DURING RENDER, React's documented way to reconcile state with
   *  changed inputs. This was an effect keyed on [initialTarget], which ran
   *  after the commit - so there was a painted frame showing the OLD
   *  percentage against the NEW target - and it cost a set-state-in-effect
   *  error for the privilege. */

  /** Which screen the panel is showing.
   *
   *  `summary` is where you ARRIVE, and it is the whole point of the
   *  redesign: the decomposition is already computed, so it is the panel
   *  rather than something you read out of a grid. `plates` is the
   *  adjustment surface, one tap behind it, for the gym that is missing a 15.
   */
  const [screen, setScreen] = useState<"summary" | "plates">("summary");

  /** Which of the summary's two pickers is open, if either.
   *
   *  They EXPAND IN PLACE rather than navigating, so the answer, the barbell
   *  and Apply all stay on screen and update live while you change the bar or
   *  take a percentage. NEITHER OF THEM CLOSES ITSELF on a tap either - both
   *  are rows you look at, use, and then dismiss with Back. The bar one used
   *  to delete itself on the first selection while its identical twin stayed
   *  put, which nothing on screen explained.
   *  The old bar control was a single cell you tapped to cycle
   *  20 -> 15 -> 10 -> 7.5 -> wrap, which made the neighbour in one direction
   *  a single tap and the neighbour in the other direction three, and never
   *  said what the choices were. Its replacement on the plate grid is a
   *  clamped stepper; see the note on that cell.
   */
  const [expanded, setExpanded] = useState<"bar" | "percent" | null>(null);

  /** The percentage currently applied, or null at 100% of the arrival target.
   *
   *  THE PERCENTAGES ARE OF THE TARGET YOU ARRIVED WITH, never of the last
   *  percentage. That is what makes a warm-up ramp work: 50, then 70, then 90
   *  are three percentages of the working weight, not a weight decaying
   *  towards nothing. 100 is one of the chips, so coming back is a tap rather
   *  than a memory test.
   *
   *  DERIVED, not state. It is already written down: localTarget over the
   *  arrival target IS the percentage. Holding it separately meant the header
   *  kept saying "80% of 100" after the lifter went back to the numpad and
   *  typed something new - localTarget reset on its own, the plates re-solved
   *  at full weight, and the only thing still claiming a percentage was the
   *  line telling them what they were looking at. Deriving it makes that
   *  impossible rather than fixed, and costs two setState-in-effect calls
   *  less.
   */
  const percent =
    localTarget == null || initialTarget <= 0
      ? null
      : Math.round((localTarget / initialTarget) * 100);



  const [counts, setCounts] = useState<number[]>(() => {
    if (initialTarget <= 0) return denoms.map(() => 0);
    return decomposeTargetClosest(initialTarget, bar, unit).counts;
  });

  /** A NEW NUMBER FROM THE NUMPAD IS A NEW PROBLEM: drop any percentage,
   *  drop the hand-edit flag, AND RE-SOLVE THE BAR.
   *
   *  It used to do the first two and leave `counts` alone, which left the
   *  bar loaded for the PREVIOUS target with nothing on screen admitting it.
   *  A workout-unit flip lands in exactly that state every time: the panel
   *  sees the new unit one render BEFORE the host finishes converting the
   *  value, so the unit block below solves a 100 KG target as 100 LB, and
   *  then this block watches the real 220.46 LB arrive and clears two flags
   *  without touching a plate. The panel read TARGET 220.46 LB over a 100 LB
   *  bar, the chip called it 120 LB under, and Apply wrote 100.
   *  Found by an adversarial pass over the five device frames.
   *
   *  When the unit block below fires in the same render its own setCounts
   *  runs after this one and wins, which is right - that is the one that
   *  knows the new bar.
   *
   *  Adjusted DURING RENDER, the documented way to reconcile state with
   *  changed inputs. This was an effect keyed on [initialTarget], which ran
   *  after the commit - so there was a painted frame showing the OLD
   *  percentage against the NEW target - and it cost a set-state-in-effect
   *  error for the privilege. */
  const [seenTarget, setSeenTarget] = useState(initialTarget);
  if (seenTarget !== initialTarget) {
    setSeenTarget(initialTarget);
    setLocalTarget(null);
    setPlatesEdited(false);
    setCounts(
      initialTarget > 0
        ? decomposeTargetClosest(initialTarget, bar, unit).counts
        : denoms.map(() => 0),
    );
  }

  useEffect(() => {
    // Re-solve from `effectiveTarget` (which carries a percentage if one is
    // applied), NOT `initialTarget` - decomposing from the original silently
    // discarded the percentage on a bar switch, so Apply wrote the full
    // weight. deps stay [bar]; the current target is read from the closure.
    //
    // ONLY WHILE STILL SOLVING. See platesEdited above.
    if (effectiveTarget <= 0 || platesEdited) return;
    const decomposed = decomposeTargetClosest(effectiveTarget, bar, unit);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- bar cycle is an external state change
    setCounts(decomposed.counts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bar]);

  /** A CHANGE OF WORKOUT UNIT IS A CHANGE OF HARDWARE.
   *
   *  The lifter can flip the session between kg and lb from the workout
   *  header without closing the keypad - that control carries
   *  data-keypad-coexists precisely so it stays reachable mid-entry - and
   *  this panel stayed mounted through it with everything seeded from the
   *  old unit.
   *
   *  Nothing about that survives the flip. `bar` held 20, which is not in
   *  BAR_WEIGHTS.lb, so the picker highlighted nothing and the panel was
   *  solving against a bar that does not exist. `counts` is an array of
   *  indices into PLATE_SETS, and those sets are DIFFERENT HARDWARE - kg is
   *  25/20/15, lb is 45/35/25 - so the same array silently re-read one 25kg
   *  plate as one 45lb one. A 100kg target came out as a 160lb total.
   *
   *  So it starts again: the unit's own default bar, and a fresh solve.
   *  Found by an adversarial pass rather than on a device, but the path is
   *  a supported one. */
  const [seenUnit, setSeenUnit] = useState(unit);
  if (seenUnit !== unit) {
    setSeenUnit(unit);
    const nextBar = defaultBarWeight(unit);
    setBar(nextBar);
    setPlatesEdited(false);
    setCounts(
      initialTarget > 0
        ? decomposeTargetClosest(initialTarget, nextBar, unit).counts
        : PLATE_SETS[unit].map(() => 0),
    );
  }

  const total = computeTotal(counts, bar, unit);
  // v9.261 — diff/hasTarget read from `effectiveTarget` so the
  // inline overlay's localTarget overrides flow through to the
  // target-vs-total comparison + the TARGET button label.
  const targetDiff =
    effectiveTarget > 0
      ? Math.round((total - effectiveTarget) * 100) / 100
      : 0;
  const hasTarget = effectiveTarget > 0;
  const totalIsEmpty = total === 0;
  // v9.260 - `xButtonInactive`, `allZero`, `targetCounts`,
  // `matchesTargetLayout` and `resetAllCounts` went with the X
  // reset button itself. Clearing is now the Reset button on the plate
  // grid, and the long-press that replaced the X for a while is gone
  // too - it was undiscoverable and it raised the iOS text menu.
  // Re-solving on a new target is the render-time seenTarget block, not
  // an effect: the only effect left has deps [bar] and does not watch
  // initialTarget at all, so do not go looking for it there.

  function incrementPlate(idx: number) {
    haptics.light();
    // Adding a plate by hand is the moment this panel stops solving.
    setPlatesEdited(true);
    setCounts((prev) => {
      const next = [...prev];
      next[idx] = (next[idx] ?? 0) + 1;
      return next;
    });
  }

  function decrementPlate(idx: number) {
    haptics.light();
    // Taking one off counts too - a lifter stripping back to what the rack actually has is still setting the load themselves.
    setPlatesEdited(true);
    setCounts((prev) => {
      const next = [...prev];
      next[idx] = Math.max(0, (next[idx] ?? 0) - 1);
      return next;
    });
  }

  /** Clear every plate. The replacement for a ~500ms hold on each cell,
   *  which was undiscoverable and was also the gesture that raises the iOS
   *  long-press text menu - the one reported from a device as "the box fields
   *  are still clickable when you press and hold". A named button on the
   *  screen that can make the mess does the same job and says so.
   *
   *  The bar is deliberately left alone: it is not a plate, and nobody
   *  clearing a mis-tapped stack means to go back to a 20kg bar they chose. */
  function handleResetAll() {
    haptics.medium();
    setCounts(denoms.map(() => 0));
    // Reset clears the intent as well as the plates. "Start again" means the
    // panel goes back to solving, so the next bar change re-solves rather
    // than preserving an empty bar the lifter did not choose.
    setPlatesEdited(false);
  }

  // v9.257 — handleBarCycle removed alongside the BAR cell that
  // called it. Bar weight is now selected by tap on the inline
  // option stack inside BarbellVisualisation; that calls
  // `handleSelectBar` below which sets the discrete value
  // directly (no cycle).
  function handleSelectBar(next: number) {
    // Tapping the bar you are already on does nothing, the same guard the
    // percent row has. setBar with the current value would bail out of the
    // render anyway, so nothing is at stake except a haptic that lied.
    if (next === bar) return;
    haptics.light();
    setBar(next);
  }

  function handleApply() {
    haptics.medium();
    onApply(total);
  }

  // ONE unit label, used everywhere: the hero, the target, the chip, the
  // plate cells, Apply and every aria-label. This comment used to name
  // `unitLabel` twice and claim the two could differ, which was the
  // residue of the deleted display-unit toggle.
  const unitLabel = unit.toUpperCase();
  const formatWeight = (n: number) =>
    Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/\.?0+$/, "");
  const totalDisplay = formatWeight(total);
  const targetDisplay = formatWeight(effectiveTarget);

  const perSidePlates = denoms
    .map((denom, idx) => ({ denom, count: counts[idx] ?? 0 }))
    .filter((p) => p.count > 0);
  /** Is there anything for Reset to clear? A bare bar has nothing, and a
   *  control that does nothing is worse than no control. */
  const hasPlates = perSidePlates.length > 0;

  /** The two neighbours of the current bar, for the stepper on the plate
   *  grid. BAR_WEIGHTS runs heaviest first, so the LEFT arrow walks left
   *  through the list exactly as it reads in the picker.
   *
   *  null means the end of the list and a dimmed arrow. Clamped rather than
   *  wrapping: the cell this replaced cycled one way only, which put the
   *  neighbour behind you three taps away, and wrapping would put it three
   *  taps away again.
   *
   *  An off-list bar cannot happen through any path this panel has - setBar
   *  only ever takes a list entry or defaultBarWeight - but cycleBarWeight
   *  guards for it, so both arrows go home rather than both going dead. */
  const barList = BAR_WEIGHTS[unit];
  const barIdx = barList.indexOf(bar);
  const heavierBar =
    barIdx < 0 ? barList[0]! : barIdx > 0 ? barList[barIdx - 1]! : null;
  const lighterBar =
    barIdx < 0
      ? barList[0]!
      : barIdx < barList.length - 1
        ? barList[barIdx + 1]!
        : null;

  // v9.245 — plate colours sourced from module-scope PLATE_COLOURS
  // table (consumed by the active-cell fill AND the SVG plate fill
  // so the barbell visualisation paints plates in their real-world
  // powerlifting-plate colours).
  const plateColours = unit === "kg" ? PLATE_COLOURS_KG : PLATE_COLOURS_LB;

  // v9.245.4 — plate-cell active-state styling. When a plate is
  // active (count > 0), the cell fills with the plate's real
  // colour; text contrast adapts per plate (dark text on light
  // backgrounds — white + yellow — light text on the rest).
  // Falls back to brand-orange when the denom isn't in the colour
  // map (defensive — shouldn't happen with current PLATE_SETS but
  // keeps the visual safe if a custom plate denomination is
  // added later).
  function plateActiveStyle(colour: string | undefined): CSSProperties {
    if (!colour) {
      return {
        backgroundColor: "var(--color-brand)",
        color: "var(--color-on-brand)",
      };
    }
    const isLightPlate = colour === "#ffffff" || colour === "#eab308";
    return {
      backgroundColor: colour,
      // Dark ink on the pale plates (white/yellow), theme-agnostic — was
      // var(--color-bg) which flipped to near-white (invisible) in light.
      color: isLightPlate ? "var(--color-on-brand)" : "#ffffff",
      // The white plate (#ffffff) vanishes on the light-theme surface
      // (bg-surface is white too) — give it a hairline edge. border-box
      // keeps the cell size unchanged; harmless on dark.
      ...(colour === "#ffffff"
        ? { border: "1px solid var(--color-border)" }
        : null),
    };
  }

  // The per-side breakdown and its shrink ladder lived here. They are gone
  // with the per-side hero: the barbell draws the plates, labels each one and
  // captions the per-side total, so nothing needed the string. Roughly 50
  // lines, including a grouping rule and a three-step font ladder that only
  // existed because the string could get long enough to wrap.

  /** THE ONE STATUS SLOT, on every screen, always in the same place.
   *
   *  Always a brand fill with dark ink, including when the bar cannot make
   *  the target. The state is carried by the LEAD WORD rather than by colour,
   *  because this is a statement of what you are about to load and not a
   *  warning - a 1 kg shortfall on a 100 kg squat is information, and drawing
   *  it in a second colour would make the panel look like it had gone wrong.
   *  Owner's call, made by looking at both drawn side by side.
   *
   *  Parallel wording across all three so the shape is learnable at a glance:
   *  the news, then the load. "EXACT", "1 KG UNDER", "1 KG OVER".
   */
  // THE VERDICT ONLY. It used to carry the total as well - "Exact · 35 KG" -
  // because the hero was the per-side breakdown and the total had nowhere
  // else to be. The hero IS the total now, 40px directly above this, so
  // repeating it here made the chip the second-biggest statement of a number
  // the reader had just finished looking at.
  const statusText = !hasTarget
    ? `Loaded`
    : targetDiff === 0
      ? `Exact`
      : `${formatWeight(Math.abs(targetDiff))} ${unitLabel} ${
          targetDiff < 0 ? "under" : "over"
        } target`;

  const statusChip = (
    <span
      className="inline-flex items-center rounded-md bg-brand text-on-brand px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider tabular-nums"
      role="status"
    >
      {statusText}
    </span>
  );

  /** Percentages of the ARRIVAL target. Six fits one row at 390px, and the
   *  set brackets both jobs: the low end is a warm-up ramp, the high end a
   *  back-off set, and 100 is the way back. */
  const PERCENT_STEPS = [50, 60, 70, 80, 90, 100] as const;

  function handleSelectPercent(pct: number) {
    // TAPPING THE ONE YOU ARE ALREADY ON DOES NOTHING.
    //
    // Without this, picking 100 when no percentage was applied re-solved the
    // plates from the target and wiped a stack the lifter had set by hand -
    // the same complaint as the bar change, reached a different way. And
    // 100 is the easy one to hit by accident: it is the selected segment, at
    // the end of the row, on a control you opened to look rather than to
    // change.
    //
    // Guarded on the CURRENT percentage rather than on the number 100, so it
    // holds for every segment. Re-selecting 80 while already at 80 is just
    // as much of a no-op, and going 80 -> 100 is a real change that still
    // re-solves. It is also what the shared SegmentedPicker does, which this
    // row is hand-rolled to keep its 44px height.
    if (pct === (percent ?? 100)) return;
    haptics.light();
    const next = Math.round(initialTarget * pct) / 100;
    setLocalTarget(pct === 100 ? null : next);
    // Asking for 80% is asking the panel to solve again, so it does.
    setPlatesEdited(false);
    // Re-solve HERE rather than in an effect. The bar effect exists because a
    // bar change is external; this one is the direct result of the tap, and
    // routing it through an effect would be a set-state-in-effect for no
    // reason.
    setCounts(decomposeTargetClosest(next, bar, unit).counts);
  }


  /** Up one level, and there are three of them.
   *
   *  The bottom-left button is the only caller: one control, one ladder,
   *  picker -> summary -> numpad. See the note on that button for why the
   *  header chip that used to call this as well is gone.
   *
   *  One label, "BACK", for both inner levels. Three different words for
   *  three levels reads as three different kinds of action; one word that
   *  always means the same thing does not. NUMPAD keeps its name because it
   *  is the only one that leaves the calculator, and naming where you are
   *  going is worth it when you are going somewhere else.
   */
  function handleBack() {
    haptics.light();
    if (expanded !== null) {
      setExpanded(null);
      return;
    }
    if (screen === "plates") {
      setScreen("summary");
      return;
    }
    onBackToKeypad();
  }

  /** Header: the target, centred, and nothing else in the row.
   *
   *  It carried a back chip in the left rail as well, which made TWO ways
   *  back on every screen - the chip and the bottom-left button - and on the
   *  plate grid the two did different things, because the bottom-left was
   *  Reset there. Reported from the device twice over: "there's 2 backs now?"
   *  on the pickers, and still reaching for Reset on the grid when the intent
   *  was back.
   *  One back, and it is the bottom-left button, because that is the one the
   *  thumb goes to. That empties the left rail, and a lone item held against
   *  the right edge of an otherwise empty row reads as the leftover of a
   *  layout rather than as a layout - so the target takes the axis.
   *
   *  A plain centred block, not a three-column grid. With nothing in the
   *  rails there is nothing to balance against, and `text-center` cannot go
   *  missing the way a JIT grid template can. See
   *  src/components/centring-grids.test.ts for what that failure looks like.
   */
  const header = (
    <div className="mx-3 mt-2 relative text-center leading-tight">
      {/* WHICH PLATES. Not a switcher - a statement.

          The owner went looking for the KG/LB switch here and there is none,
          because there cannot be one: this panel counts PHYSICAL plates, and
          a control that converted denominations offered a 55.12 lb plate that
          no gym owns. That version existed and was deleted.
          What was missing was the answer to the question behind the reach -
          which plates am I looking at - and the left rail has been empty since
          the back chip moved to the footer. A flat chip with no press state and
          no border-radius games reads as a label; the unit lives in the session,
          one screen up.
          Absolutely positioned so it cannot move the centred target off the
          sheet axis, which is the whole reason the target is centred. */}
      <span
        aria-hidden
        className="absolute left-0 top-1/2 -translate-y-1/2 text-fg-dim text-[9px] font-semibold uppercase tracking-[0.09em] pointer-events-none"
      >
        {unitLabel} plates
      </span>
      <div className="text-fg-dim text-[9px] font-semibold uppercase tracking-[0.1em]">
        {percent != null ? `${percent}% of target` : "Target"}
      </div>
      <div className="text-brand-text text-[15px] font-bold tabular-nums tracking-wide whitespace-nowrap">
        {hasTarget ? `${targetDisplay} ${unitLabel}` : "—"}
      </div>
    </div>
  );

  const barbell = (
    <BarbellVisualisation
      perSidePlates={perSidePlates}
      bar={bar}
      unit={unit}
      barPickerOpen={expanded === "bar"}
      // The grid gets the running total as a caption; the summary does not,
      // because it has the 40px version of the same number directly above.
      totalLabel={screen === "plates" ? `${totalDisplay} ${unitLabel}` : undefined}
      // The band on the shaft. Both screens get it: it is a property of the
      // bar rather than of the screen, and the shaft is a fixed length in
      // the one-sided drawing, so it always fits and the unit never has to
      // be dropped.
      barLabel={`${formatWeight(bar)} ${unitLabel}`}
    />
  );

  return (
    <div
      className="relative flex-1 flex flex-col bg-surface"
      role="dialog"
      aria-label="Plate calculator"
    >
      {header}

      {screen === "summary" ? (
        <>
          {/* THE ANSWER IS THE PANEL.
              The decomposition was always computed; it just used to be
              something you worked out by reading a grid of eight cells. */}
          {/* THE HERO IS WHAT THE BAR WILL WEIGH.
              It was the per-side breakdown - "5 + 2.5" - which the barbell
              underneath now draws AND labels plate by plate, and captions
              with the per-side total besides. Three statements of the same
              fact, and the biggest type on the panel spent on the one a
              picture tells better.
              The total was only ever readable off the chip and the Apply
              button, and it is the number you are actually deciding about:
              what you are going to lift. So it takes the hero, the chip
              drops to the verdict alone, and the breakdown stays where it
              belongs - on the drawing of the bar. */}
          {/* mt-4, not mt-3. The header above is a centred label-over-number
              block too now, and at 12px apart the two read as one four-line
              stack instead of as the target and then the answer. */}
          <div className="mx-3 mt-4 text-center">
            <div className="text-fg-dim text-[10px] font-semibold uppercase tracking-[0.1em]">
              On the bar
            </div>
            <div className="text-[2.5rem] font-semibold tabular-nums tracking-tight mt-0.5">
              {totalDisplay}
              <span className="text-fg-muted text-[15px] font-semibold ml-1.5">
                {unitLabel}
              </span>
            </div>
            <div className="mt-2">{statusChip}</div>
          </div>

          {/* NO WRAPPER. BarbellVisualisation already carries its own
              mx-3 mt-2, so an outer mx-3 doubled the gutter to 24px a side
              and drew the one picture on the panel 48px narrower than the
              panel it is the subject of. */}
          {barbell}

          {/* One row, three states. The pickers take the doors' place rather
              than pushing them down, so the panel keeps its height and the
              sheet does not re-aim underneath you. */}
          {expanded === "bar" ? (
            <div className="mx-3 mt-2 mb-3">
              {/* NO LABEL, AND NO BORDER, so this row is EXACTLY the height of
                the doors row it replaces - 44px, same margins, same corner.
                Open it and not one pixel moves: the bar, the hero and the
                chip stay put and change value as you tap through.
                It carried "BAR" above it, which cost about 20px and said
                what four bar weights in a row already say. That 20px was
                what made the row feel like something appearing and
                disappearing rather than a row that changes what it offers.
                The border went for the same reason - the doors are a
                borderless surface-2 fill, and 1px top and bottom is 2px of
                difference, which is enough to see when it is the only thing
                moving on the panel. */}
              {/* Same joined track as the percent row. Four mutually
                  exclusive values, same shape of decision, same slot on the
                  panel - there is no reason for them to be drawn two ways,
                  and they were.

                  AND IT STAYS OPEN, which is the other half of that. It used
                  to delete itself on the first tap while the percent row -
                  drawn identically, in the same slot, with the same orange
                  segment - stayed put. Nothing on screen said which one was
                  about to vanish under your thumb; you found out by tapping.
                  Staying open is also the better behaviour on its own terms:
                  tap through 20, 15, 10 and watch the band on the shaft and
                  the hero change under each one. Back puts the doors back. */}
              <div
                className="flex rounded-xl bg-surface-2 overflow-hidden"
                role="group"
                aria-label="Bar weight"
              >
                {BAR_WEIGHTS[unit].map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => handleSelectBar(b)}
                    aria-pressed={b === bar}
                    className={
                      // THE HOUSE ACTIVE STATE, not a brand fill. See the
                      // note on the percent row below for why both rows
                      // changed together.
                      "flex-1 h-11 text-[13px] font-medium tabular-nums transition-colors " +
                      (b === bar
                        ? "bg-fg/10 text-fg font-semibold border-b-2 border-brand"
                        : "text-fg-muted hover:text-fg hover:bg-fg/5 active:bg-fg/10 border-b-2 border-transparent")
                    }
                  >
                    {formatWeight(b)}
                    <span
                      className={
                        "text-[10px] ml-1 " +
                        (b === bar ? "opacity-70" : "text-fg-dim")
                      }
                    >
                      {unitLabel}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : expanded === "percent" ? (
            // mb-3 like its two siblings: the commit bar's seam needs room
            // ABOVE it, and mt-auto on the footer cannot also be a margin, so
            // the space has to come from whichever row is showing.
            <div className="mx-3 mt-2 mb-3">
              {/* Label out, for the same reason as the bar picker's. "PERCENT
                OF 36 KG" is also no longer needed to say what OF: the header
                reads "80% of target" the moment one is applied, and the
                target sits next to it. */}
              {/* A JOINED TRACK, not six loose pills.
                  Six mutually exclusive values ARE a segmented control, and
                  separate pills understate that - they read as six actions
                  when they are one choice.
                  The track is the house recipe, `border border-border` over
                  `bg-surface-2`, which is declared and works. I had told the
                  owner this was blocked on --color-surface-3 because a
                  segmented control needs a recessed groove; that was wrong.
                  The house track is a RAISED plate and no control in this app
                  has ever had a groove.
                  Kept at h-11 rather than the house 38px, because these are
                  not a preference you set once - each tap recomputes the
                  whole panel - so they keep the 44px floor.

                  THE SELECTED SEGMENT IS THE HOUSE STATE AFTER ALL. It was
                  a brand fill, defended here on the grounds that at six
                  across a 2px line is a thin answer to which one am I on.
                  That was reading the house recipe as a line alone, and it
                  is not: every SegmentedPicker in the app pairs the brand
                  underline with a LIFT, a segment one step lighter than
                  its track. Two signals, and the fill is the one you see
                  first. The lift here is `bg-fg/10` rather than the chip
                  size's `bg-surface-2`, because this track has to stay
                  surface-2 to match the doors row it replaces without
                  moving a pixel - so the step has to go up from there, and
                  --color-surface-3 does not exist.
                  Both branches carry `border-b-2` so the two states are
                  the same height, which is what keeps the row at 44.
                  Reported from a device: five orange objects on one panel
                  with the bar picker open. This is two of them. */}
              <div
                className="flex rounded-xl bg-surface-2 overflow-hidden"
                role="group"
                // THE ARRIVAL TARGET, not the effective one. The visible
                // percentages are of initialTarget by construction (see
                // the note on `percent` above), so once 80% is applied,
                // reading out "Percent of 80 KG" names a base that none of
                // the six buttons actually uses.
                aria-label={`Percent of ${formatWeight(initialTarget)} ${unitLabel}`}
              >
                {PERCENT_STEPS.map((pct) => {
                  const on = (percent ?? 100) === pct;
                  return (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => handleSelectPercent(pct)}
                      aria-pressed={on}
                      aria-label={`${pct} percent of target`}
                      className={
                        "flex-1 h-11 text-[13px] tabular-nums transition-colors " +
                        (on
                          ? "bg-fg/10 text-fg font-semibold border-b-2 border-brand"
                          : "text-fg-muted hover:text-fg hover:bg-fg/5 active:bg-fg/10 border-b-2 border-transparent")
                      }
                    >
                      {pct}
                      <span className="text-[10px] ml-0.5 opacity-70">%</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mx-3 mt-2 mb-3 flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => {
                  haptics.light();
                  setScreen("plates");
                }}
                className={
                  // FILLED AND QUIET, not outlined. There were five buttons
                  // in two stacked rows at the foot of this panel and FOUR of
                  // them were outlined - these three and Cancel - so the eye
                  // read four peers and one orange one, when the truth is
                  // three doors, one abandon and one commit. Reported as the
                  // row feeling like it was in the wrong place; it was not
                  // the place, it was the company it was keeping.
                  //
                  // A fill puts them in the same family as the plate cells:
                  // things you tap to change what is on the bar. The outline
                  // is left to Cancel alone, where it means leave without
                  // doing this.
                  "flex-1 h-11 rounded-xl bg-surface-2 hover:bg-fg/5 active:bg-fg/10 text-fg-muted hover:text-fg text-sm font-medium transition-colors"
                }
              >
                Adjust plates
              </button>
              <button
                type="button"
                onClick={() => {
                  haptics.light();
                  setExpanded("bar");
                }}
                className="flex-1 h-11 rounded-xl bg-surface-2 hover:bg-fg/5 active:bg-fg/10 text-fg-muted hover:text-fg text-sm font-medium tabular-nums transition-colors"
              >
                Adjust bar
              </button>
              {/* The ONE job the deleted CALC panel did that nothing else does
                  in a single tap. Its other mode was a second numpad, reached
                  from a numpad, and its percent mode duplicated the keypad's
                  own - which already exists and already dims itself on fields
                  where a percentage means nothing. */}
              <button
                type="button"
                onClick={() => {
                  haptics.light();
                  setExpanded("percent");
                }}
                disabled={!hasTarget}
                aria-label="Take a percentage of the target"
                className={
                  // NAMED, AND THE SAME WIDTH AS THE OTHER TWO. It was a
                  // 48px box containing a lone "%", next to two buttons that
                  // say what they do - so the row read as two doors and a
                  // symbol, and the symbol was the one nobody would press.
                  // Naming it costs nothing: at 14px the three labels are
                  // about 92, 68 and 54px inside 118px each.
                  "flex-1 h-11 rounded-xl bg-surface-2 text-sm font-medium transition-colors " +
                  (hasTarget
                    ? "hover:bg-fg/5 active:bg-fg/10 text-fg-muted hover:text-fg"
                    : // Dimmed IN PLACE. Dropping the fill as well would make
                      // the disabled door vanish into the panel, and a door
                      // that is not there is a different statement from one
                      // that is not available yet.
                      "text-fg-dim/50 cursor-not-allowed")
                }
              >
                Percent
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          {/* THE BARBELL STAYS ON THE ADJUSTMENT SCREEN, which is the one
              screen where you are changing what is on the bar and therefore
              the one that most needs to show it. It redraws on every tap. */}
          {/* THE CHIP GOES ABOVE THE BARBELL, as it does on the summary.
              It was below, which put it 6px under a barbell box that has
              dead space at the bottom and 10px above the plate grid - so it
              read as a label belonging to the plates rather than as the
              panel's status, and it looked crowded against the first row of
              cells. Reported from the device as sitting too low and too
              close to the plates.
              Both screens now read the same way down the page: what the bar
              will weigh, then the bar, then the controls. */}
          {/* RESET LIVES HERE NOW, not in the bottom-left corner.
              The bottom-left is Back on the summary and on both pickers, so
              on the one screen where it cleared the bar the thumb had learned
              the wrong thing about it and kept arriving. Reported from the
              device after the pickers were fixed: still tapping Reset when
              the intent was back.
              It sits beside the status chip because the chip is the thing
              that tells you there is a mess - "11 KG OVER TARGET" - and it is
              gated on there being plates to clear, so it is never a control
              that does nothing. Equal flex-1 rails keep the chip on the
              sheet's axis whether or not Reset is showing. */}
          {/* h-11 ON THE ROW, not on its contents, for two reasons.
              Reset mounts and unmounts with hasPlates, and without a fixed
              height the row was 32px with it and 24 without - so emptying
              the last plate moved the header and the chip 8px up a sheet
              whose height is measured live.
              And the button needs 44px. The first cut was 32, which is the
              floor this file defends twice over for the digit cells. The
              target is the whole 44 and only the chip inside it is painted,
              which is the construction the minus badge on a plate cell
              already uses: 44x44 of touch around a small mark. */}
          <div className="mx-3 mt-3 h-11 flex items-center">
            <div className="flex-1 flex justify-start">
              {hasPlates && (
                <button
                  type="button"
                  onClick={handleResetAll}
                  // The accessible name has to CONTAIN the visible one or a
                  // spoken "tap Reset" matches nothing. "Clear all plates"
                  // did not.
                  aria-label="Reset all plates"
                  className="group h-11 -ml-2 px-2 flex items-center"
                >
                  {/* IT NEEDS A SURFACE. The first cut was a bare dim word,
                      kept quiet on purpose so it would not read as a back
                      chip - and it came back from the device reading as a
                      label instead, which is the same fault the other way
                      round. It was the only tappable thing on the panel
                      with nothing drawn underneath it. */}
                  <span className="h-7 px-2.5 rounded-lg bg-surface-2 border border-border text-fg-muted group-hover:text-fg group-active:bg-fg/10 text-[11px] font-semibold uppercase tracking-wider flex items-center transition-colors">
                    Reset
                  </span>
                </button>
              )}
            </div>
            <div className="shrink-0">{statusChip}</div>
            <div className="flex-1" />
          </div>
          {barbell}

          {/* THREE ACROSS, NOT FOUR.
              At four the cell is 87px wide and the minus button owns 44 of
              them - half the cell, with the boundary running through the
              middle of where a thumb aims to ADD. Both targets cleared 44px,
              which is why the first pass kept four columns, but "clears the
              floor" and "is not next to the other one" are different
              questions and only the first was checked.
              Three columns make it 118px, so the minus owns 37%, and the
              cells go to 72px tall with room for the numerals to grow.
              Measured cost: 94px, spent on the one screen you reach by
              tapping a button rather than by swiping. */}
          <div className="grid grid-cols-3 gap-1.5 px-3 mt-2.5 mb-3">
            {denoms.map((denom, idx) => {
              const count = counts[idx] ?? 0;
              const active = count > 0;
              const colour = plateColours[denom];
              const denomLabel = formatWeight(denom);
              return (
                <div key={denom} className="relative">
                  <button
                    type="button"
                    onClick={() => incrementPlate(idx)}
                    aria-label={`Add one ${denom} ${unitLabel} plate per side. Currently ${count}.`}
                    style={active ? plateActiveStyle(colour) : undefined}
                    className={
                      "w-full h-[4.5rem] rounded-xl flex flex-col items-center justify-center transition-colors duration-75 active:scale-95 " +
                      (active
                        ? ""
                        : "bg-surface-2 hover:bg-fg/5 active:bg-fg/10 text-fg-dim")
                    }
                  >
                    {/* WHICH NUMBER IS THE LOUD ONE DEPENDS ON THE STATE.
                        Both lines were fixed sizes, so an empty cell's
                        biggest, brightest glyph was a zero - and a row of
                        four unloaded cells read as "0 0 0 0" with the
                        denominations whispering above them. When you are
                        scanning this grid to ADD a plate, the thing you are
                        looking for is "15", and the zero tells you nothing
                        you did not already know from the cell being dark.
                        Loaded is the other way round: the plate colour has
                        already said which plate it is, so the count is the
                        news. */}
                    <span
                      className={
                        "tabular-nums leading-none " +
                        (active
                          ? "text-[11px] font-medium"
                          : "text-[15px] font-semibold text-fg-muted")
                      }
                    >
                      {denomLabel}
                    </span>
                    <span
                      className={
                        "tabular-nums leading-none mt-1.5 " +
                        (active
                          ? "text-[17px] font-semibold"
                          : "text-[11px] text-fg-dim")
                      }
                    >
                      {count}
                    </span>
                  </button>
                  {/* A colour rail on the EMPTY cells, so you can still find
                      the plate you want before you load it. The loaded cell
                      does not need one; it is that colour all over. */}
                  {!active && colour && (
                    <span
                      aria-hidden
                      className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r pointer-events-none"
                      style={{ backgroundColor: colour }}
                    />
                  )}
                  {/* MINUS IS A REAL 44px TARGET, not a 20px glyph, and it is
                      drawn at every count including zero.
                      The badge is 26px and the button around it is 44x44, so
                      the touch area meets the floor while the mark stays small
                      enough to leave the count as the loud thing. Hiding it at
                      zero - which is what shipped - made a fresh cell look
                      like it only went up.
                      Long-press-to-reset is gone with it. It was the gesture
                      nobody found, and the one that also raises the iOS text
                      menu. Reset does that job and says so. */}
                  <button
                    type="button"
                    onClick={() => decrementPlate(idx)}
                    tabIndex={count === 0 ? -1 : undefined}
                    aria-hidden={count === 0 ? true : undefined}
                    aria-label={`Remove one ${denom} ${unitLabel} plate per side`}
                    className={
                      "absolute top-0 right-0 w-11 h-11 flex items-start justify-end p-1.5 " +
                      // NOT `disabled`, WHICH WOULD EAT THE TAP.
                      //
                      // This sits over the top-right 44x44 of an 87x64 cell,
                      // so a disabled button does not fire its own onClick AND
                      // does not let the press reach the cell underneath - it
                      // just swallows it. That would make half the top of
                      // every EMPTY cell dead to the one action an empty cell
                      // has, which is to add a plate.
                      //
                      // pointer-events-none lets the press fall through to the
                      // cell, so an empty cell is +1 everywhere. Found by
                      // measuring the two rects against the real stylesheet,
                      // not by reading the code.
                      (count === 0 ? "pointer-events-none" : "")
                    }
                  >
                    <span
                      aria-hidden
                      className={
                        "w-[26px] h-[26px] rounded-full flex items-center justify-center text-base leading-none font-semibold transition-colors " +
                        (count === 0
                          ? "bg-bg/20 text-fg-dim/40"
                          : active
                            ? "bg-bg/30 text-current"
                            : "bg-bg/40 text-fg-muted")
                      }
                    >
                      &minus;
                    </span>
                  </button>
                </div>
              );
            })}
            {/* THE BAR IS A STEPPER, NOT A DOOR.
                One tap used to set the screen to the summary AND open the bar
                picker - two levels of forward travel from a cell that sits
                among seven that do not travel at all. Back from there lands
                on the summary rather than here, so nudging the bar mid-build
                meant a round trip through a screen you were not going to.
                Nothing was lost on the way, since the stack survives and the
                re-solve is guarded on platesEdited, but it was a journey
                nobody asked for.

                Four bar weights is a stepper's worth of choice, and the cell
                is 268px wide at 430: two 44px targets and 180px of readout.

                CHEVRONS, NOT PLUS AND MINUS. Minus already means take a plate
                off, on all seven cells directly above this one, and two
                meanings for one glyph four pixels apart is the fault this
                panel keeps making.

                The summary keeps Adjust bar, for seeing all four at once. */}
            <div
              className="col-span-2 h-[4.5rem] rounded-xl bg-surface-2 flex items-center"
              role="group"
              aria-label="Bar weight"
            >
              <button
                type="button"
                onClick={() => heavierBar != null && handleSelectBar(heavierBar)}
                disabled={heavierBar == null}
                aria-label={`Heavier bar. Currently ${formatWeight(bar)} ${unitLabel}.`}
                className={
                  // disabled IS right here, unlike the minus badge on a plate
                  // cell: that one sits on top of the cell it would swallow
                  // taps for, and this one has its own 44px with nothing
                  // underneath. Out of the tab order, announced unavailable.
                  "w-11 self-stretch rounded-l-xl flex items-center justify-center transition-colors " +
                  (heavierBar == null
                    ? "text-fg-dim/30 cursor-not-allowed"
                    : "text-fg-muted hover:text-fg hover:bg-fg/5 active:bg-fg/10")
                }
              >
                {/* THE ICON, NOT THE GUILLEMET. `&lsaquo;` is 7px wide at
                    text-xl and 10px at text-3xl - it is a punctuation mark,
                    and it stays a punctuation mark however large the type
                    is. Measured. A stroked chevron at w-5 is the mark this
                    control needs, since the arrow is the whole of its
                    content rather than a lead-in to a word. */}
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              {/* A WELL, NOT A CELL. The middle is 179px at 430 and it is
                  the only region of this grid that does nothing when you
                  press it - and it was drawn exactly like the seven cells
                  around it that do. A darker fill reads as a display and
                  leaves the two raised ends reading as buttons, which is
                  what a stepper looks like everywhere else.
                  The two hairlines that used to fence it off have gone
                  with it: the change of fill IS the boundary now, and a
                  line on top of it would be saying the same thing twice.
                  bg-bg/30 is the family the minus badges on the plate
                  cells already use, and unlike a surface-3 it exists. */}
              <div className="flex-1 self-stretch bg-bg/30 flex flex-col items-center justify-center">
                <span className="text-fg-dim text-[10px] font-semibold uppercase tracking-wider leading-none">
                  Bar
                </span>
                <span className="text-fg text-[17px] font-semibold tabular-nums leading-none mt-1.5">
                  {formatWeight(bar)}
                  <span className="text-fg-muted text-[11px] font-semibold ml-1">
                    {unitLabel}
                  </span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => lighterBar != null && handleSelectBar(lighterBar)}
                disabled={lighterBar == null}
                aria-label={`Lighter bar. Currently ${formatWeight(bar)} ${unitLabel}.`}
                className={
                  "w-11 self-stretch rounded-r-xl flex items-center justify-center transition-colors " +
                  (lighterBar == null
                    ? "text-fg-dim/30 cursor-not-allowed"
                    : "text-fg-muted hover:text-fg hover:bg-fg/5 active:bg-fg/10")
                }
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Commit bar. Reset only exists on the screen that can make a mess, and
          it is the replacement for the long-press that used to do it per cell
          with nothing on screen to say so. */}
      {/* THE GAP ABOVE THIS IS PADDING, NOT THE AUTO MARGIN.
          `mt-auto` was the only thing separating the commit row from the
          content, and an auto margin is not spacing - it is the leftover, and
          it resolves to zero when there is none.
          THERE IS NEVER ANY. The sheet's `min-h-[400px]` is a BORDER-BOX
          minimum (box-sizing is border-box globally), and the sheet spends
          1px on its top border and 26px on the grab handle before this panel
          gets anything - so the floor is worth 373px here, and the shortest
          state of this panel is 388, twelve more than it was before the
          barbell box was matched to its viewBox. Add a safe-area inset on a
          notched phone
          and it is worth 339. The floor has never bound in any state on any
          device, so mt-auto has always been 0 and every screen's last row has
          always sat flush against these buttons.
          An earlier version of this note claimed the summary had 24px going
          spare. That came from a measurement that left out the border and the
          grab handle. It was wrong, and it would have sent the next person
          looking for slack that does not exist.
          The numpad never shows it because its key grid carries p-2, so it
          has 8px of its own below the last row. Same footer classes, different
          neighbour - which is why this is not fixed by copying the numpad.
          pt-3 cannot collapse, so the gap is now exactly 12px everywhere.
          shrink-0 is not insurance: nothing above this row is compressible
          (every item is a fixed height), so if a ceiling is ever added to this
          sheet, this row is what overflows whether it shrinks or not. It is
          here to say the row is fixed chrome, and a ceiling would need a
          scroll region above it, not a smaller footer. */}
      <div className="mt-auto shrink-0 border-t border-seam pt-3 mx-3 mb-2 flex items-center gap-2">
        {/* THE BOTTOM-LEFT BUTTON IS THE ONLY WAY BACK, AND IT ALWAYS GOES UP
            EXACTLY ONE LEVEL.
            It used to make three different promises from the same corner. With
            a picker open it was Back; on the plate grid it was Reset; at the
            top it was Cancel, which closed the whole keypad. Meanwhile a chip
            in the header offered a second, different way back on every screen.
            Reported from the device twice: "there's 2 backs now?" on the
            pickers, and then, once those were fixed, still reaching for Reset
            on the grid. That second one is the tell - the thumb had learned
            this corner meant back on three screens out of four and kept coming
            here on the fourth, where it cleared the bar.
            So: one back, living where the thumb already goes, and the header
            chip is gone. Reset moved up beside the status chip, on the one
            screen that can make a mess.

            THE TOP OF THE LADDER IS THE NUMPAD, NOT NOTHING. That is where you
            came from and it is where the target gets retyped, so it is the
            honest parent of this screen - and it used to be reachable only
            through the chip that just went. Closing the sheet is no longer
            what this corner does; drag-down, a tap outside and Esc all still
            dismiss, and the numpad one tap away has its own Close. */}
        <button
          type="button"
          onClick={handleBack}
          aria-label={
            expanded !== null || screen === "plates"
              ? "Back to the summary"
              : "Back to the numpad"
          }
          className="flex-1 h-11 rounded-xl border border-border hover:border-fg-dim text-fg-muted hover:text-fg text-sm font-medium flex items-center justify-center gap-1 transition-colors"
        >
          {/* The chevron is the whole point of the change: one glance says
              UP, whatever the word after it happens to be. */}
          <span aria-hidden className="text-base leading-none">
            &lsaquo;
          </span>
          {expanded !== null || screen === "plates" ? "Back" : "Numpad"}
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={totalIsEmpty}
          aria-label={`Apply ${totalDisplay} ${unitLabel} to keypad`}
          className={
            "flex-[2] h-11 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors " +
            (totalIsEmpty
              ? "bg-surface-2 text-fg-dim cursor-not-allowed"
              : "bg-brand hover:bg-brand-strong active:bg-brand-strong text-on-brand")
          }
        >
          <span aria-hidden className="text-lg leading-none">
            &#10003;
          </span>
          <span className="text-base">
            Apply {totalDisplay} {unitLabel}
          </span>
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * BarbellVisualisation
 *
 * ONE SIDE OF THE BAR, seen from the middle outwards: shaft, collar,
 * plates heaviest-first, end cap. Not both sides.
 *
 * ## Why one side
 *
 * Measured at 366px of panel, with the old two-sided drawing: a plate
 * rendered 17.3px wide at 60kg and at 140kg alike, then 18.5 at four 25s
 * a side, 16.8 at five and 15.4 at six. The picture got WORSE the more
 * you loaded, which is backwards - a heavy bar is the one you most want
 * to check before you get under it.
 *
 * The cause is which constraint binds. Up to three plates a side the
 * drawing is limited by the fixed 108px box HEIGHT; past that the viewBox
 * grows wider than the panel and width takes over, shrinking everything.
 * Lengthening the bar makes that worse, not better - the SVG scales to
 * fit, so a longer shaft shrinks the plates with it.
 *
 * Drawing one side halves the width the plates have to share, which puts
 * every load back on the height clamp. The plate is then a flat 27px
 * whatever is on the bar, and plate WIDTH stops being something the
 * layout decides and becomes something we choose - hence the wider
 * 12-30 range below, which is free once width is no longer binding.
 *
 * The symmetry was never carrying information. You do not load one side
 * differently from the other, and you were reading the same stack twice.
 *
 * ## The two labels
 *
 * The bar weight rides in a BAND on the shaft, which is where a real bar
 * carries it. It used to be a caption centred under the whole picture -
 * so as plates filled the width the words stayed in the middle while the
 * shaft shrank away beneath them, and at four plates a side "Bar 20 KG"
 * was sitting under a wall of 25s reading as if it labelled them. One
 * side fixes that outright: the shaft is a FIXED length here, so the band
 * always fits and the unit never has to be dropped.
 *
 * "Per side" is HTML, not SVG text, for the reason the old label row was:
 * SVG text scales with the viewBox. It is also doing real work - a
 * one-sided bar with no marker could be read as the whole bar, and that
 * is a 2x error in the direction that hurts.
 *
 * Plates auto-sort biggest-to-smallest from the collar outwards, the way
 * they are actually loaded.
 * ─────────────────────────────────────────────────────────────── */

export function BarbellVisualisation({
  perSidePlates,
  bar,
  unit,
  barPickerOpen = false,
  barLabel,
  totalLabel,
}: {
  perSidePlates: Array<{ denom: number; count: number }>;
  bar: number;
  unit: WeightUnit;
  /** Brand-fills THE BAND while the bar picker is open, so the row of
   *  choices and the thing it changes are visibly the same subject.
   *
   *  It used to paint the SHAFT, which was 340x8 of brand orange - the
   *  exact shape this app draws a rest drain with - and took the panel to
   *  five orange objects at once. Reported from a device.
   *  The band is the better target anyway: it is the bar weight, printed,
   *  which is precisely what the row changes. 44x20 cannot read as a
   *  track, and the highlight lands on the number rather than on the half
   *  of the bar nobody is loading. */
  barPickerOpen?: boolean;
  /** What the band on the shaft reads, e.g. "20 KG". Passed in because the
   *  caller already formats the bar weight for Apply and the picker, not
   *  because it knows a different unit - it does not, there is only the
   *  workout unit now. */
  barLabel?: string;
  /** The running total, e.g. "35 KG", captioned bottom-LEFT opposite the
   *  per-side line. Only the plate grid passes it: the summary carries the
   *  same number at 40px directly above this drawing, and the grid - the
   *  one screen where you are actually changing what is on the bar - had
   *  it nowhere but on the Apply button.
   *  It costs no height. The bottom-left of this box has always been empty
   *  while every other corner of it was doing something. */
  totalLabel?: string;
}) {
  // Flatten per-side counts into an ordered stack, heaviest first: that is
  // the order they go on, against the collar and working out.
  const stack: number[] = [];
  for (const { denom, count } of perSidePlates) {
    for (let i = 0; i < count; i++) stack.push(denom);
  }
  stack.sort((a, b) => b - a);

  const perSideTotal = stack.reduce((sum, denom) => sum + denom, 0);
  const unitLabel = unit.toUpperCase();
  const plateColours =
    unit === "kg" ? PLATE_COLOURS_KG : PLATE_COLOURS_LB;

  /** THE VIEWBOX IS A CONSTANT WIDTH, and the shaft is what absorbs the
   *  difference.
   *
   *  The obvious build lets the viewBox shrink with the stack, and then an
   *  empty bar draws 136 units wide inside a box scaled for 406 - so it
   *  renders about a third of the width, centred, with a lake of empty
   *  space either side. Measured before it shipped: 122px of drawing in a
   *  366px box.
   *
   *  Fixing the total and deriving the shaft fixes three things at once.
   *  The drawing always fills the panel. The scale is constant, so a plate
   *  is 27px and the band text 10px at EVERY load rather than swelling on
   *  a light bar. And a longer shaft on a light bar is honest: the half
   *  you are not loading really is longer when there is less on it.
   *
   *  406 IS 406px OF PANEL, because the box below is 120 tall and so is
   *  SVG_H. It used to be a 108px box against a 120 viewBox, which is a
   *  0.9 fit, so the drawing rendered 366 wide and sat centred in the 406
   *  with 20px of nothing either side - a letterbox nobody asked for, and
   *  11% off every plate. Matching the two reclaims it: a 25 goes 27px to
   *  30px and four of them a side draw 388 of the 406 rather than 370.
   *  Measured both ways at 430 before changing it.
   */
  const TOTAL_W = 406;
  const MIN_HALF_BAR = 78;
  const COLLAR_W = 8;
  const CAP_W = 10;
  const PAD = 8;
  const SVG_H = 120;
  const cy = SVG_H / 2;
  const maxDenom = unit === "kg" ? 25 : 45;

  /** 48-86, not 36-86. The floor is raised so the SMALLEST plate still
   *  fills most of the box: at 36 a 2.5 drew a third of the height and
   *  landed within 5px of a 5, so two different plates looked like the
   *  same plate twice. A 25 still towers over a 1.25, by less. */
  const plateH = (denom: number) => 48 + Math.min(1, denom / maxDenom) * 38;
  /** 20-30, and the FLOOR is set by the LABEL rather than by looks.
   *
   *  The narrowest plate has to carry the longest string, because 1.25 is
   *  four characters and 25 is two. At the old 12-unit floor a 2.5 rendered
   *  12px wide against a label needing 15, so the small plates carried no
   *  number - which is most of the point of drawing them.
   *  18 fixed that at the 0.9 scale the box used to impose. Now the box
   *  matches the viewBox and everything renders 11% larger, INCLUDING the
   *  type, so 18 was back to about 2px of slack on a 1.25. 20 gives it a
   *  20.5px plate and 2.4px of room, and lifts a 10 over the 24-unit line
   *  into the larger label tier as a bonus. Measured at 430, all four ways.
   *  A 25 is still visibly fatter than a 1.25, and nearly twice as tall. */
  const plateW = (denom: number) => 20 + Math.min(1, denom / maxDenom) * 10;

  // Width cap, not a count cap: eight 25s occupy the room twelve 10s do, so
  // capping by count either wasted space or hid too much. 300 units keeps
  // the plate at or above 24px rendered, which is the floor for a label. At
  // the 20-unit floor that is 15 of the smallest plates a side, which is not
  // a bar anybody loads.
  const MAX_STACK_WIDTH = 300;
  const visibleStack: number[] = [];
  let cumulative = 0;
  for (const denom of stack) {
    const w = plateW(denom);
    if (cumulative + w > MAX_STACK_WIDTH) break;
    visibleStack.push(denom);
    cumulative += w;
  }
  const hiddenPlateCount = stack.length - visibleStack.length;
  const stackWidth = visibleStack.reduce((sum, d) => sum + plateW(d), 0);

  // The shaft takes whatever the plates leave, down to a floor that still
  // holds the band.
  const HALF_BAR = Math.max(
    MIN_HALF_BAR,
    TOTAL_W - COLLAR_W - stackWidth - CAP_W - PAD,
  );
  const collarX = HALF_BAR;
  const platesX = collarX + COLLAR_W;
  const svgW = Math.max(TOTAL_W, platesX + stackWidth + CAP_W + PAD);

  type PlateRender = { denom: number; x: number; w: number; h: number };
  const plates: PlateRender[] = [];
  let cursor = platesX;
  for (const denom of visibleStack) {
    const w = plateW(denom);
    plates.push({ denom, x: cursor, w, h: plateH(denom) });
    cursor += w;
  }
  const capX = cursor;

  const ariaLabel =
    stack.length === 0
      ? `Empty barbell, ${bar} ${unitLabel}`
      : `One side of the barbell: ${stack.length} ${
          stack.length === 1 ? "plate" : "plates"
        }, ${perSideTotal} ${unitLabel} per side, on a ${bar} ${unitLabel} bar`;

  // The 5 KG plate fills #ffffff, which disappears on a light surface.
  const plateStroke = (denom: number) =>
    plateColours[denom] === "#ffffff"
      ? "#9ca3af"
      : "var(--color-border)";
  // Dark ink on the pale plates, light on the rest. Theme-agnostic, because
  // the plate colours do not change between themes.
  const plateInk = (denom: number) => {
    const c = plateColours[denom];
    return c === "#ffffff" || c === "#eab308"
      ? "rgba(0,0,0,0.7)"
      : "rgba(255,255,255,0.9)";
  };

  /** Band geometry. Sized to the text so a "7.5 KG" bar does not overflow
   *  a box cut for "20 KG", and clamped to the shaft so it can never spill
   *  onto the collar. */
  const bandW = Math.min(
    HALF_BAR - 16,
    Math.max(34, (barLabel?.length ?? 0) * 7 + 14),
  );

  return (
    // NO mx-3 HERE. Both call sites already wrap this in one, so the barbell
    // sat 24px in from the left while the chip, the doors, the grid and the
    // commit bar all started at 12. Visible the moment you sight down the
    // edge of the panel, and invisible in any single screenshot.
    <div>
      {/* A FIXED BOX. The SVG's rendered height varies with the load, and
          the sheet is measured live - so an unpinned box meant every plate
          tap resized the sheet, republished --keypad-bottom-offset and
          re-fired the keypad's aim, which only ever scrolls one way. The
          workout list ratcheted downwards as plates went on and never came
          back when they came off. Pinning it makes the sheet's height
          independent of what is loaded.

          120, NOT 108, AND IT HAS TO MATCH SVG_H. A shorter box against a
          120 viewBox is a 0.9 fit, and the SVG centres what it cannot
          fill - so the drawing was 366 wide inside 406 with 20px of empty
          either side, and every plate was 11% smaller than the geometry
          above says it is.
          The alternative was to shorten SVG_H to 108 instead, which gets
          the same plates for no extra height. It loses on the one case
          that matters: at four 25s a side the tallest plate already comes
          within 0.3px of the PER SIDE caption below it, and shortening the
          viewBox puts it 4px THROUGH it. Growing the box moves the caption
          down with the box and leaves 2px. Measured, all three ways. */}
      <div className="relative h-[120px] flex items-center">
        <svg
          viewBox={`0 0 ${svgW} ${SVG_H}`}
          className="w-full h-auto max-h-full"
          role="img"
          aria-label={ariaLabel}
        >
          {/* Shaft. Runs off the left edge on purpose: this is the middle
              of the bar, and the half you are not loading is not drawn. */}
          <rect
            x={-4}
            y={cy - 4}
            width={HALF_BAR + 4}
            height={8}
            rx={3}
            fill="var(--color-fg-dim)"
          />
          {/* Collar: the step the plates load against. */}
          <rect
            x={collarX}
            y={cy - 13}
            width={COLLAR_W}
            height={26}
            rx={2}
            fill="var(--color-fg-muted)"
          />
          {plates.map((p, i) => (
            <g key={`${p.denom}-${i}`}>
              <rect
                x={p.x}
                y={cy - p.h / 2}
                width={p.w}
                height={p.h}
                rx={2}
                fill={plateColours[p.denom] ?? "var(--color-brand)"}
                stroke={plateStroke(p.denom)}
                strokeWidth={0.75}
              />
              {/* The number ON the plate. Colour-coding is real but it only
                  works if the gym runs IPF colours, and plenty of kit is
                  all-black rubber; a printed number is true everywhere, and
                  it is what tells a 5 from a 2.5 at any size. */}
              <text
                x={p.x + p.w / 2}
                y={cy}
                textAnchor="middle"
                dominantBaseline="central"
                // Three steps, set by MEASURING the label against the
                // plate rather than by eye: "1.25" is four characters on
                // the narrowest plate there is, and at 9 it overflowed its
                // 16.7px by a third of a pixel.
                fontSize={p.w >= 24 ? 11 : p.w >= 19 ? 9 : 8}
                fontWeight={700}
                fill={plateInk(p.denom)}
              >
                {p.denom}
              </text>
            </g>
          ))}
          {/* End cap, so the stack reads as ending rather than as cut off. */}
          <rect
            x={capX + 2}
            y={cy - 13}
            width={CAP_W}
            height={26}
            rx={2}
            fill="var(--color-fg-muted)"
          />
          {/* The bar weight, in a band on the shaft. */}
          {barLabel && (
            <>
              <rect
                x={HALF_BAR / 2 - bandW / 2}
                y={cy - 10}
                width={bandW}
                height={20}
                rx={3}
                fill={
                  barPickerOpen
                    ? "var(--color-brand)"
                    : "var(--color-fg-muted)"
                }
              />
              <text
                x={HALF_BAR / 2}
                y={cy}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={11}
                fontWeight={700}
                // on-brand, not bg. Dark ink is right on both the grey band
                // and the orange one, and --color-bg flips to near-white in
                // the light theme while --color-on-brand does not.
                fill="var(--color-on-brand)"
              >
                {barLabel}
              </text>
            </>
          )}
          {/* DRAWN INSIDE THE VIEWBOX. It used to sit at capX + CAP_W + 6,
              which is past the right edge whenever the stack is long enough
              to be truncated - so the one case it exists for was the one
              case it was clipped in, and a cut-off stack looked complete.
              Placed over the last plate instead, where there is always room
              because the cap is the thing that fixes the right edge. */}
          {hiddenPlateCount > 0 && (
            <text
              x={svgW - PAD - CAP_W - 4}
              y={cy}
              textAnchor="end"
              dominantBaseline="central"
              fontSize={12}
              fontWeight={700}
              fill="var(--color-fg)"
            >
              {`+${hiddenPlateCount}`}
            </text>
          )}
        </svg>
        {/* HTML, not SVG text, because SVG text scales with the viewBox and
            this has to stay the same size at every load. It is also load
            bearing: without it a one-sided bar can be read as the whole
            bar, which is a 2x error in the direction that hurts. */}
        {totalLabel && (
          <span
            aria-hidden
            className="absolute left-0 bottom-0 text-fg-muted text-[11px] font-semibold uppercase tracking-[0.08em] pointer-events-none"
          >
            On the bar{" "}
            <span className="text-fg font-bold tabular-nums">{totalLabel}</span>
          </span>
        )}
        <span
          aria-hidden
          // IT ENDS WHERE THE BAR ENDS, not where the panel does.
          //
          // At right-0 it lined up with the right edge of Percent and of
          // Apply, which sounded right and read wrong: the end cap stops 6px
          // short of that, so the caption carried on past the thing it is
          // captioning. Reported from a device as the text sitting too far
          // right, which it was, by exactly PAD.
          //
          // The offset is PAD + CAP_W as a PERCENTAGE of the viewBox, not a
          // pixel count, because the drawing scales with the panel: the same
          // 18 units are 18px at 430 and 15.6 at 375. Expressed this way it
          // lands on the outside face of the last plate at every width and
          // every load, since svgW is fixed and the stack is anchored to the
          // cap. It also happens to centre the line over the Percent door to
          // within 2px, which is where the eye wanted it - but that is a
          // coincidence of this row, and the plate grid has no doors at all.
          style={{ right: `${((PAD + CAP_W) / svgW) * 100}%` }}
          className="absolute bottom-0 text-fg-muted text-[11px] font-semibold uppercase tracking-[0.08em] pointer-events-none"
        >
          {perSideTotal > 0 ? `${perSideTotal} ${unitLabel} per side` : "Per side"}
        </span>
      </div>
    </div>
  );
}
